import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { rmApi } from '../../services/api';
import { 
  FaArrowLeft, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaMoneyBillWave, 
  FaExclamationTriangle,
  FaPrint,
  FaCheck,
  FaTimes,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';

function InvoiceDetails() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  
  // Reference for the printable invoice section
  const printableRef = useRef(null);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch (e) {
      return dateString; // Fallback
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Fetch invoice details on component mount
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      setLoading(true);
      setError('');
      
      try {
        // In a real implementation, we'd call an API to get invoice details
        // For now, let's simulate an API call with some delay
        setTimeout(() => {
          // Sample data - in a real app, replace with API call
          const mockInvoice = {
            id: invoiceId,
            invoice_no: `INV-${invoiceId.padStart(5, '0')}`,
            dso_name: 'Sample DSO',
            dso_mobile: '9876543210',
            dso_pan: 'ABCDE1234F',
            amount: 15000,
            gst_amount: 2700, // 18% of 15000
            total_amount: 17700, // amount + gst
            case_count: 3,
            created_at: new Date().toISOString(),
            invoice_status: 0, // 0=Pending, 1=Approved, 2=Rejected
            invoice_status_str: 'Pending',
            payment_status: 0, // 0=Generated, 1=Completed, 2=Failed
            payment_status_str: 'Generated',
            payment_date: null,
            cases: [
              {
                id: 1,
                lead_name: 'John Doe',
                application_no: 'APP123456',
                product_name: 'Credit Card',
                amount: 5000
              },
              {
                id: 2,
                lead_name: 'Jane Smith',
                application_no: 'APP789012',
                product_name: 'Personal Loan',
                amount: 5000
              },
              {
                id: 3,
                lead_name: 'Alex Johnson',
                application_no: 'APP345678',
                product_name: 'Credit Card',
                amount: 5000
              }
            ]
          };
          
          setInvoice(mockInvoice);
          setLoading(false);
        }, 1000);
        
        // In production, use something like:
        // const response = await rmApi.getInvoiceDetails(invoiceId);
        // setInvoice(response.data);
        
      } catch (err) {
        console.error("Error fetching invoice details:", err);
        setError('Failed to load invoice details.');
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoiceId]);

  // Action handlers
  const handleApprove = async () => {
    setActionLoading(true);
    setError('');
    setActionSuccess('');
    
    try {
      // In a real implementation, call the API
      // await rmApi.updateInvoiceStatus(invoiceId, 'Approved');
      
      // Simulate API call
      setTimeout(() => {
        setInvoice(prev => ({
          ...prev,
          invoice_status: 1,
          invoice_status_str: 'Approved'
        }));
        setActionSuccess('Invoice approved successfully.');
        setActionLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error approving invoice:", err);
      setError('Failed to approve invoice. Please try again.');
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    setError('');
    setActionSuccess('');
    
    try {
      // In a real implementation, call the API
      // await rmApi.updateInvoiceStatus(invoiceId, 'Rejected');
      
      // Simulate API call
      setTimeout(() => {
        setInvoice(prev => ({
          ...prev,
          invoice_status: 2,
          invoice_status_str: 'Rejected'
        }));
        setActionSuccess('Invoice rejected successfully.');
        setActionLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error rejecting invoice:", err);
      setError('Failed to reject invoice. Please try again.');
      setActionLoading(false);
    }
  };

  const handlePaymentComplete = async () => {
    setActionLoading(true);
    setError('');
    setActionSuccess('');
    
    try {
      // In a real implementation, call the API
      // await rmApi.updateInvoicePaymentStatus(invoiceId, 1);
      
      // Simulate API call
      setTimeout(() => {
        setInvoice(prev => ({
          ...prev,
          payment_status: 1,
          payment_status_str: 'Completed',
          payment_date: new Date().toISOString()
        }));
        setActionSuccess('Payment marked as completed.');
        setActionLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error updating payment status:", err);
      setError('Failed to update payment status. Please try again.');
      setActionLoading(false);
    }
  };

  // Function to handle printing the invoice
  const handlePrintInvoice = () => {
    const printContents = printableRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    const printStyles = `
      @media print {
        body { font-family: Arial, sans-serif; color: #333; }
        .invoice-container { padding: 20px; }
        .invoice-header { margin-bottom: 30px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .text-center { text-align: center; }
        .text-end { text-align: right; }
        .invoice-summary { margin-top: 20px; margin-left: auto; width: 300px; }
        .invoice-status { display: none; }
        .action-buttons-container { display: none; }
      }
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Invoice - ${invoice?.invoice_no || 'Invoice'}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          <div class="print-container">${printContents}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Function to handle payment failed
  const handlePaymentFailed = async () => {
    setActionLoading(true);
    setError('');
    setActionSuccess('');
    
    try {
      // In a real implementation, call the API
      // await rmApi.updateInvoicePaymentStatus(invoiceId, 2); // Failed status
      
      // Simulate API call
      setTimeout(() => {
        setInvoice(prev => ({
          ...prev,
          payment_status: 2,
          payment_status_str: 'Failed',
          payment_date: new Date().toISOString()
        }));
        setActionSuccess('Payment marked as failed.');
        setActionLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error updating payment status:", err);
      setError('Failed to update payment status. Please try again.');
      setActionLoading(false);
    }
  };
  
  // Function to toggle payment status
  const handleTogglePaymentStatus = async () => {
    setActionLoading(true);
    setError('');
    setActionSuccess('');
    
    try {
      const newStatus = invoice.payment_status === 1 ? 0 : 1; // Toggle between Completed (1) and Generated (0)
      const newStatusStr = newStatus === 1 ? 'Completed' : 'Generated';
      
      // In a real implementation, call the API
      // await rmApi.updateInvoicePaymentStatus(invoiceId, newStatus);
      
      // Simulate API call
      setTimeout(() => {
        setInvoice(prev => ({
          ...prev,
          payment_status: newStatus,
          payment_status_str: newStatusStr,
          payment_date: newStatus === 1 ? new Date().toISOString() : null
        }));
        setActionSuccess(`Payment marked as ${newStatusStr.toLowerCase()}.`);
        setActionLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error updating payment status:", err);
      setError('Failed to update payment status. Please try again.');
      setActionLoading(false);
    }
  };

  // Function to render status badge
  const renderStatusBadge = (status, type = 'approval') => {
    let variant = '';
    switch (status?.toLowerCase()) {
      case 'approved':
        variant = 'success';
        break;
      case 'rejected':
        variant = 'danger';
        break;
      case 'pending':
        variant = 'warning';
        break;
      case 'completed':
        variant = 'success';
        break;
      case 'failed':
        variant = 'danger';
        break;
      case 'generated':
        variant = 'primary';
        break;
      default:
        variant = 'secondary';
    }
    
    return <Badge bg={variant} className="status-badge">{status}</Badge>;
  };

  return (
    <div className="invoice-details-page">
      <div className="page-header">
        <div className="left-section">
          <Link to="/rm/invoices" className="back-link">
            <FaArrowLeft /> Back to Invoices
          </Link>
          <h2>Invoice Details</h2>
          
          
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <Spinner animation="border" role="status" variant="primary" />
          <span>Loading invoice details...</span>
        </div>
      ) : error ? (
        <Alert variant="danger">
          <FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      ) : invoice ? (
        <>
          {actionSuccess && (
            <Alert variant="success" className="mb-4 action-alert" dismissible onClose={() => setActionSuccess('')}>
              {actionSuccess}
            </Alert>
          )}
          
          {/* Action Buttons */}
          <div className="action-buttons-container">
            <div className="approval-section">
              {invoice.invoice_status_str?.toLowerCase() === 'pending' && (
                <>
                  <Button 
                    variant="success" 
                    onClick={handleApprove} 
                    disabled={actionLoading}
                    className="action-button approve-button"
                  >
                    <FaCheck className="me-2" />
                    Approve Invoice
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={handleReject} 
                    disabled={actionLoading}
                    className="action-button reject-button"
                  >
                    <FaTimes className="me-2" />
                    Disapprove Invoice
                  </Button>
                </>
              )}
              
              {invoice.invoice_status_str?.toLowerCase() === 'approved' && 
                invoice.payment_status_str?.toLowerCase() === 'generated' && (
                <>
                  <Button 
                    variant="success" 
                    onClick={handlePaymentComplete} 
                    disabled={actionLoading}
                    className="action-button payment-button"
                  >
                    <FaMoneyBillWave className="me-2" />
                    Set Paid
                  </Button>
                  <Button 
                    variant="warning" 
                    onClick={handlePaymentFailed} 
                    disabled={actionLoading}
                    className="action-button payment-fail-button"
                  >
                    <FaTimes className="me-2" />
                    Set Payment Failed
                  </Button>
                </>
              )}
              
              {actionLoading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </div>
            
            <div className="button-group">
              {invoice.invoice_status_str?.toLowerCase() !== 'pending' && (
                <Button
                  variant={invoice.payment_status === 1 ? "success" : "secondary"}
                  onClick={handleTogglePaymentStatus}
                  disabled={actionLoading || invoice.payment_status === 2}
                  className="action-button payment-toggle-button"
                >
                  {invoice.payment_status === 1 ? (
                    <>
                      <FaToggleOn className="me-2" />
                      Paid
                    </>
                  ) : (
                    <>
                      <FaToggleOff className="me-2" />
                      Unpaid
                    </>
                  )}
                </Button>
              )}
              <Button 
                variant="primary" 
                onClick={handlePrintInvoice} 
                className="print-button"
              >
                <FaPrint className="me-2" />
                Print Invoice
              </Button>
            </div>
          </div>
          
          {/* Printable Invoice */}
          <div ref={printableRef} className="printable-invoice">
            <Container className="mt-4 mb-5">
              <Card className="invoice-card p-4 shadow-sm">
                <div className="invoice-title-section">
                  <div className="company-logo">
                    <img src="/static/images/Wishfin-logo.png" alt="Wishfin Logo" className="logo-image" />
                  </div>
                  <h2 className="text-center invoice-heading mb-0">INVOICE</h2>
                  <div className="invoice-meta">
                    <div className="invoice-number">
                      <span className="meta-label">Invoice #:</span>
                      <span className="meta-value">{invoice.invoice_no}</span>
                    </div>
                    <div className="invoice-date">
                      <span className="meta-label">Date:</span>
                      <span className="meta-value">{formatDate(invoice.created_at)}</span>
                    </div>
                    {invoice.payment_date && (
                      <div className="payment-date">
                        <span className="meta-label">Payment Date:</span>
                        <span className="meta-value">{formatDate(invoice.payment_date)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="invoice-parties">
                  <Row>
                    <Col md={6} className="party-section">
                      <div className="party-header">Sender Details:</div>
                      <div className="party-content">
                        <p><span className="party-label">Name:</span> {invoice.dso_name || 'New Agent'}</p>
                        <p><span className="party-label">Address:</span> None</p>
                        <p><span className="party-label">Mobile:</span> {invoice.dso_mobile || '9373002277'}</p>
                        <p><span className="party-label">PAN:</span> {invoice.dso_pan || 'GSHJH6543K'}</p>
                      </div>
                    </Col>

                    <Col md={6} className="party-section">
                      <div className="party-header">Recipient Details:</div>
                      <div className="party-content">
                        <p><span className="party-label">Name:</span> Owner D</p>
                        <p><span className="party-label">Address:</span> Ghaziabad</p>
                        <p><span className="party-label">Mobile:</span> 9812345678</p>
                        <p><span className="party-label">Email:</span> d@gmail.com</p>
                        <p><span className="party-label">GST No:</span> 36AAACH7408R1Z2</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                <hr className="invoice-divider" />

                <Table bordered responsive className="invoice-items-table">
                  <thead className="table-header">
                    <tr>
                      <th>Product Name</th>
                      <th className="text-center">Number of Leads</th>
                      <th className="text-end">Total Payout Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.cases ? (
                      invoice.cases.map((item, index) => (
                        <tr key={item.id}>
                          <td>{item.product_name}</td>
                          <td className="text-center">1</td>
                          <td className="text-end">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>SBI SimplyClick Credit Card</td>
                        <td className="text-center">{invoice.case_count || 1}</td>
                        <td className="text-end">{formatCurrency(invoice.amount)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="invoice-summary">
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value">{formatCurrency(invoice.amount)}</span>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label">GST (18%):</span>
                      <span className="summary-value">{formatCurrency(invoice.amount * 0.18 || invoice.gst_amount)}</span>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="text-end">
                      <span className="summary-label grand-total">Grand Total:</span>
                      <span className="summary-value grand-total">{formatCurrency(invoice.amount * 1.18 || invoice.total_amount)}</span>
                    </Col>
                  </Row>
                </div>

                <hr className="invoice-divider" />

                <div className="bank-details">
                  <h5>Recipient's Bank Details:</h5>
                  <div className="bank-details-grid">
                    <p><span className="bank-label">Account Name:</span> {invoice.dso_name || 'agent new'}</p>
                    <p><span className="bank-label">Bank Name:</span> HDFC</p>
                    <p><span className="bank-label">Account Number:</span> 6139164949</p>
                    <p><span className="bank-label">IFSC Code:</span> HDFC0006553</p>
                    <p><span className="bank-label">Branch Name:</span> None</p>
                  </div>
                </div>
                
                <div className="invoice-footer">
                  <p className="thank-you">Thank you for your business!</p>
                  <p className="footer-note">This is a computer generated invoice, no signature required.</p>
                </div>
              </Card>
            </Container>
          </div>
        </>
      ) : (
        <Alert variant="warning">
          Invoice not found or you don't have permission to view it.
        </Alert>
      )}
      
      <style jsx>{`
        .invoice-details-page {
          padding: 20px;
          background-color: #f8fafc;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        /* Page Header Styles */
        .page-header {
          margin-bottom: 20px;
        }
        
        .left-section {
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }
        
        .back-link {
          color: #4b5563;
          text-decoration: none;
          margin-bottom: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: #1f2937;
        }
        
        .page-header h2 {
          margin: 0 0 12px 0;
          font-size: 1.4rem;
          font-weight: 600;
          color: #111827;
        }
        
        /* Status Indicators */
        .invoice-status {
          display: flex;
          gap: 16px;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        
        .status-badge-container {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .status-label {
          font-weight: 500;
          color: #4b5563;
          font-size: 0.85rem;
        }
        
        .status-indicator {
          display: inline-block;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .status-indicator.approved {
          background-color: #dcfce7;
          color: #166534;
        }
        
        .status-indicator.rejected, .status-indicator.failed {
          background-color: #fee2e2;
          color: #991b1b;
        }
        
        .status-indicator.pending, .status-indicator.generated {
          background-color: #fef3c7;
          color: #92400e;
        }
        
        .status-indicator.completed {
          background-color: #dcfce7;
          color: #166534;
        }
        
        
        
        /* Loading State */
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          color: #6b7280;
        }
        
        /* Action Buttons Container */
        .action-buttons-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .approval-section {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .button-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        @media (max-width: 768px) {
          .action-buttons-container {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .button-group {
            margin-top: 10px;
            align-self: flex-start;
          }
        }
        
        .action-button {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 4px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .approve-button {
          background-color: #dcfce7;
          border-color: #bbf7d0;
          color: #166534;
        }
        
        .approve-button:hover:not(:disabled) {
          background-color: #bbf7d0;
        }
        
        .reject-button {
          background-color: #fee2e2;
          border-color: #fecaca;
          color: #991b1b;
        }
        
        .reject-button:hover:not(:disabled) {
          background-color: #fecaca;
        }
        
        .payment-button {
          background-color: #dbeafe;
          border-color: #bfdbfe;
          color: #1e40af;
        }
        
        .payment-button:hover:not(:disabled) {
          background-color: #bfdbfe;
        }
        
        .payment-fail-button {
          background-color: #fef3c7;
          border-color: #fde68a;
          color: #92400e;
        }
        
        .payment-fail-button:hover:not(:disabled) {
          background-color: #fde68a;
        }
        
        .payment-toggle-button.btn-success {
          background-color: #10b981;
          border-color: #059669;
          color: white;
        }
        
        .payment-toggle-button.btn-success:hover:not(:disabled) {
          background-color: #059669;
        }
        
        .payment-toggle-button.btn-secondary {
          background-color: #9ca3af;
          border-color: #6b7280;
          color: white;
        }
        
        .payment-toggle-button.btn-secondary:hover:not(:disabled) {
          background-color: #6b7280;
        }
        
        .print-button {
          background-color: #f3f4f6;
          border-color: #d1d5db;
          color: #111827;
        }
        
        .print-button:hover {
          background-color: #e5e7eb;
        }
        
        /* Success Alert */
        .action-alert {
          border-radius: 6px;
        }
        
        /* Printable Invoice Styles */
        .printable-invoice {
          background-color: #fff;
        }
        
        .invoice-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }
        
        /* Invoice Title Section */
        .invoice-title-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          position: relative;
        }
        
        .company-logo {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .logo-image {
          height: 40px;
          width: auto;
          object-fit: contain;
        }
        
        .invoice-heading {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          font-size: 1.8rem;
          font-weight: 700;
          color: #111827;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .invoice-meta {
          display: flex;
          flex-direction: column;
          gap: 4px;
          text-align: right;
        }
        
        .invoice-number, .invoice-date, .payment-date {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        
        .meta-label {
          color: #6b7280;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .meta-value {
          font-weight: 600;
          color: #111827;
          font-size: 0.9rem;
        }
        
        /* Invoice Parties */
        .invoice-parties {
          margin-bottom: 20px;
        }
        
        .party-section {
          margin-bottom: 16px;
        }
        
        .party-header {
          font-size: 1.05rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }
        
        .party-content p {
          margin-bottom: 6px;
          color: #4b5563;
          font-size: 0.95rem;
        }
        
        .party-label {
          font-weight: 600;
          color: #374151;
        }
        
        /* Divider */
        .invoice-divider {
          border-top: 1px solid #e5e7eb;
          margin: 20px 0;
        }
        
        /* Invoice Items Table */
        .invoice-items-table {
          margin-bottom: 20px;
        }
        
        .table-header th {
          background-color: #f3f4f6;
          color: #374151;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        /* Invoice Summary */
        .invoice-summary {
          margin-left: auto;
          width: 350px;
          font-size: 0.95rem;
        }
        
        .summary-label {
          font-weight: 500;
          color: #4b5563;
          margin-right: 12px;
          display: inline-block;
        }
        
        .summary-value {
          font-weight: 500;
          color: #111827;
          display: inline-block;
          width: 100px;
          text-align: right;
        }
        
        .grand-total {
          font-weight: 700;
          font-size: 1rem;
          color: #111827;
        }
        
        /* Bank Details */
        .bank-details {
          background-color: #f9fafb;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .bank-details h5 {
          font-size: 1.05rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }
        
        .bank-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 12px;
        }
        
        .bank-details-grid p {
          margin-bottom: 6px;
          color: #4b5563;
          font-size: 0.95rem;
        }
        
        .bank-label {
          font-weight: 600;
          color: #374151;
          margin-right: 6px;
        }
        
        /* Invoice Footer */
        .invoice-footer {
          text-align: center;
          margin-top: 30px;
        }
        
        .thank-you {
          font-size: 1.1rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        
        .footer-note {
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
        }

        /* Print Styles */
        @media print {
          .page-header,
          .action-buttons-container,
          .action-alert {
            display: none !important;
          }
          
          .printable-invoice {
            padding: 0;
            margin: 0;
          }
          
          .invoice-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default InvoiceDetails;